/**
 * Turns a partner link into an attribution claim and makes the claim travel with the order.
 *
 * Why this exists: the modem is bought here, while the partner's commission is settled on our
 * server when Shopify reports the paid order. The order is the only record of who sent the buyer,
 * so something has to ride on it from the landing page to the payment.
 *
 * Why a claim and not the partner's code: a cart attribute is editable by the buyer. A partner
 * code written there could be swapped for another partner's, to move the commission or to get a
 * bigger discount. The claim is an opaque token our server issues for this visit, and only the
 * server knows which partner it stands for. Contract: backend-api
 * docs/domains/attribution/claim-contract.md (Bitrix 3678, 3679).
 *
 * Two paths carry it, because one is not enough:
 *
 *   1. The cart attribute. Shopify keeps attributes on an empty cart, so it works even if the
 *      visitor browses first, and copies them onto the order at checkout.
 *   2. Hidden fields inside the product form. "Buy it now" submits that form straight to checkout
 *      WITHOUT going through the cart, so a cart attribute alone would be dropped on that path.
 *
 * The claim is deliberately NOT gated on marketing cookie consent: partners are paid whether or not
 * the buyer accepted marketing cookies, so attribution cannot depend on that choice.
 */

/**
 * Query parameter of the links generated in Tapfiliate. Tapfiliate's own script reads it too, to
 * count the click on their side (snippets/tll-tapfiliate.liquid).
 */
const REFERRAL_CODE_PARAM = 'ref'

/**
 * Query parameter of the links from the previous platform. Supported indefinitely: those links are
 * published in blog posts and video descriptions we do not control. The server maps the old token
 * to the partner.
 */
const VIA_TOKEN_PARAM = 'via'

/** The server rejects longer identifiers, so there is no point asking. */
const MAX_IDENTIFIER_LENGTH = 255

/** Cart attribute the claim is stored in. The webhook handler reads exactly this key. */
const CLAIM_ATTRIBUTE = 'teleleo_claim_id'

/**
 * Remembers the claim between visits, in localStorage. Buyers often come back later without the
 * partner link and buy then; with sessionStorage that purchase paid nobody (claim-contract.md, step 3).
 */
const CLAIM_STORAGE_KEY = 'tll:partner-claim'

/**
 * Remembers, for this visit only, which claim is already on the cart. A new visit may have a new
 * cart, so the stored claim is written to the cart once per visit, not on every page view.
 */
const VISIT_CLAIM_KEY = 'tll:partner-claim-on-cart'

/**
 * Remembers which partner code this visit already put on the cart.
 *
 * Every partner's buyer discount is a Shopify discount code named exactly like the partner's
 * identifier: the Tapfiliate referral code and, for old links, the `via` token. Three discounts,
 * one per partner group (B2C 20%, bloggers 10%, B2B 5%), hold all the codes (decided 12.10.2026,
 * Bitrix 4015). A new partner needs their codes added to the right discount, see
 * docs/partner-attribution.md. The discount is independent of the claim: it is applied even when
 * our server is down, and it never decides who gets the commission.
 */
const DISCOUNT_SESSION_KEY = 'tll:partner-discount'

/**
 * @typedef {{ referral_code: string } | { via_token: string }} PartnerIdentifier
 * @typedef {{ claimId: string, expiresAt: string | null, identifier: string }} StoredClaim
 */

/**
 * @returns {PartnerIdentifier | null} What the current URL says about the partner, if anything. A
 * Tapfiliate code wins over an old token when a link somehow carries both.
 */
function readIdentifierFromUrl() {
  const params = new URLSearchParams(window.location.search)
  const referralCode = params.get(REFERRAL_CODE_PARAM)?.trim()
  const viaToken = params.get(VIA_TOKEN_PARAM)?.trim()

  if (referralCode && referralCode.length <= MAX_IDENTIFIER_LENGTH) {
    return { referral_code: referralCode }
  }

  if (viaToken && viaToken.length <= MAX_IDENTIFIER_LENGTH) {
    return { via_token: viaToken }
  }

  return null
}

/**
 * @returns {string | null} Origin of our API, from the theme setting rendered in layout/theme.liquid.
 * The live theme asks production; the staging theme can point at the stage backend.
 */
function readApiOrigin() {
  const content = document
    .querySelector('meta[name="tll-attribution-api-origin"]')
    ?.getAttribute('content')
    ?.trim()

  return content ? content.replace(/\/+$/, '') : null
}

/**
 * The server sends `expires_at` as "YYYY-MM-DD HH:mm:ss" with no offset, in UTC.
 *
 * @param {string | null} expiresAt - Expiry as the server sent it.
 * @returns {boolean} Whether the claim is past its expiry. Unreadable dates count as current: the
 * server checks the expiry again on the paid order anyway.
 */
function isExpired(expiresAt) {
  if (!expiresAt) return false

  const iso = /[zZ]|[+-]\d{2}:?\d{2}$/.test(expiresAt)
    ? expiresAt.replace(' ', 'T')
    : `${expiresAt.replace(' ', 'T')}Z`
  const time = Date.parse(iso)

  return Number.isFinite(time) && time <= Date.now()
}

/**
 * @returns {StoredClaim | null} The claim remembered on an earlier page view or visit, if it is still
 * current. An expired or unreadable one is removed, so it never reaches a cart.
 */
function loadStoredClaim() {
  try {
    const stored = JSON.parse(localStorage.getItem(CLAIM_STORAGE_KEY) ?? 'null')

    if (stored && typeof stored.claimId === 'string' && stored.claimId) {
      if (!isExpired(stored.expiresAt ?? null)) return stored
    }

    localStorage.removeItem(CLAIM_STORAGE_KEY)
    return null
  } catch {
    return null
  }
}

/**
 * @param {StoredClaim} claim - The claim to remember until it expires or a new partner link replaces it.
 */
function storeClaim(claim) {
  try {
    localStorage.setItem(CLAIM_STORAGE_KEY, JSON.stringify(claim))
  } catch {
    // Private mode can throw; the claim still reaches this page's forms and the cart.
  }
}

/**
 * @returns {string | null} The claim this visit already wrote to the cart, if any.
 */
function readClaimOnCart() {
  try {
    return sessionStorage.getItem(VISIT_CLAIM_KEY)
  } catch {
    return null
  }
}

/**
 * @param {string} claimId - The claim this visit just wrote to the cart.
 */
function markClaimOnCart(claimId) {
  try {
    sessionStorage.setItem(VISIT_CLAIM_KEY, claimId)
  } catch {
    // Without the mark the next page view writes the same claim to the cart again, which is harmless.
  }
}

/**
 * Asks our server for a claim. The endpoint needs no authorisation and tells nothing about the
 * partner: only the token and its expiry come back.
 *
 * @param {string} apiOrigin - Origin of our API.
 * @param {PartnerIdentifier} identifier - Exactly one identifier, as the contract requires.
 * @returns {Promise<{ claimId: string, expiresAt: string | null }>} The issued claim.
 */
async function requestClaim(apiOrigin, identifier) {
  const response = await fetch(`${apiOrigin}/react_api/attribution/claim`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(identifier),
    credentials: 'omit',
  })

  if (!response.ok) throw new Error(`Claim request failed with status ${response.status}`)

  const payload = await response.json()
  const claimId = payload?.data?.claim_id

  if (typeof claimId !== 'string' || !claimId) throw new Error('Claim response carries no claim_id')

  return { claimId, expiresAt: payload.data.expires_at ?? null }
}

/** Tail of the queue of this page's cart writes, see queueCartWrite. */
let cartWrites = Promise.resolve()

/**
 * Runs cart writes one after another. A first-time visitor has no cart yet, and Shopify creates a
 * new cart for every request that arrives without one: written at the same time, the partner
 * discount and the claim landed in two different carts and only the discount's cart survived
 * (stg rehearsal, 22.10.2026). In a queue the first write creates the cart and the next one reuses it.
 *
 * @template T
 * @param {() => Promise<T>} write - One read-modify-write of the cart.
 * @returns {Promise<T>} The result of the write.
 */
function queueCartWrite(write) {
  const run = cartWrites.then(write)
  cartWrites = run.catch(() => {})
  return run
}

/**
 * @returns {Promise<any>} The cart as Shopify returns it from /cart.js.
 */
async function readCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
  })

  if (!response.ok) throw new Error(`Cart read failed with status ${response.status}`)

  return response.json()
}

/**
 * Records the claim on the cart and reads the cart back. Last click wins: a fresh partner link
 * replaces an earlier claim, and the server freezes the attribution once the order is paid.
 *
 * @param {string} claimId - The claim to record.
 * @returns {Promise<boolean>} Whether the cart now carries this claim.
 */
function persistOnCart(claimId) {
  return queueCartWrite(async () => {
    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [CLAIM_ATTRIBUTE]: claimId } }),
    })

    if (!response.ok) throw new Error(`Cart update failed with status ${response.status}`)

    const cart = await readCart()

    return cart?.attributes?.[CLAIM_ATTRIBUTE] === claimId
  })
}

/**
 * Adds the claim as a hidden field to one product form, so it survives a submission that bypasses
 * the cart. Idempotent: re-running on the same form only refreshes the value.
 *
 * @param {HTMLFormElement} form - A form posting to /cart/add.
 * @param {string} claimId - The claim to carry.
 */
function addFieldToForm(form, claimId) {
  const name = `attributes[${CLAIM_ATTRIBUTE}]`
  let input = form.querySelector(`input[name="${name}"]`)

  if (!input) {
    input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    form.appendChild(input)
  }

  input.value = claimId
}

/**
 * @param {string} claimId - The claim to carry.
 */
function addFieldToAllForms(claimId) {
  document
    .querySelectorAll('form[action*="/cart/add"]')
    .forEach((form) => addFieldToForm(form, claimId))
}

/**
 * Puts the partner's discount code on the cart, once per partner per visit. Shopify keeps the code
 * on the cart, empty or not, and applies it at checkout. A code that matches no discount is listed
 * as not applicable and changes nothing.
 *
 * @param {PartnerIdentifier} identifier - The partner identifier from the URL.
 */
async function applyPartnerDiscount(identifier) {
  const code = 'referral_code' in identifier ? identifier.referral_code : identifier.via_token

  try {
    if (sessionStorage.getItem(DISCOUNT_SESSION_KEY) === code) return
  } catch {
    // Private mode can throw; applying the code twice is harmless.
  }

  await queueCartWrite(async () => {
    // `discount` replaces every code on the cart, so the codes already there are sent back with
    // ours. Otherwise opening any partner link would silently drop a code the buyer typed in.
    const cart = await readCart()
    const existingCodes = (cart?.discount_codes ?? [])
      .map((discountCode) => discountCode?.code)
      .filter((existing) => typeof existing === 'string' && existing && existing !== code)

    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: [...existingCodes, code].join(',') }),
    })

    if (!response.ok) throw new Error(`Discount update failed with status ${response.status}`)
  })

  try {
    sessionStorage.setItem(DISCOUNT_SESSION_KEY, code)
  } catch {
    // See above.
  }
}

/** The claim this page carries once it is known. Read by the submit listener below. */
let currentClaimId = null

/**
 * @param {string} claimId - The claim for this visit.
 */
function carryClaim(claimId) {
  currentClaimId = claimId
  addFieldToAllForms(claimId)
}

/**
 * Puts the claim on the cart and marks the visit only once the cart has it. Unmarked, the next page
 * view of the visit writes the stored claim again.
 *
 * @param {string} claimId - The claim to record.
 */
async function recordOnCart(claimId) {
  if (await persistOnCart(claimId)) {
    markClaimOnCart(claimId)
  } else {
    console.error('The partner claim did not reach the cart; the next page view tries again')
  }
}

const identifier = readIdentifierFromUrl()
const storedClaim = loadStoredClaim()
const identifierKey = identifier ? JSON.stringify(identifier) : null
// The stored claim counts as this visit's only once this visit has put it on the cart.
const visitClaim = storedClaim && readClaimOnCart() === storedClaim.claimId ? storedClaim : null

if (identifier) {
  applyPartnerDiscount(identifier).catch((error) =>
    console.error('Failed to apply the partner discount', error),
  )
}

if (identifier && visitClaim?.identifier !== identifierKey) {
  // A partner link opened in a new visit asks for a fresh claim even when a stored one is still
  // current: the last link before the first purchase wins (claim-contract.md, step 3). Within one
  // visit the same link, e.g. on reload, reuses the claim, so the server is asked once per visit.
  const apiOrigin = readApiOrigin()

  if (apiOrigin) {
    // Losing the claim must never break the page, so failures are logged and swallowed. Nothing is
    // retried on the next page view without the link: the visitor landed once, and the server would
    // not attribute a stale claim anyway.
    requestClaim(apiOrigin, identifier)
      .then(async (claim) => {
        storeClaim({ ...claim, identifier: identifierKey })
        carryClaim(claim.claimId)
        await recordOnCart(claim.claimId)
      })
      .catch((error) => console.error('Failed to record the partner claim', error))
  }
} else if (storedClaim) {
  carryClaim(storedClaim.claimId)

  if (!visitClaim) {
    // A later visit without the link: the cart may be a new one, so the stored claim goes on it again.
    recordOnCart(storedClaim.claimId).catch((error) =>
      console.error('Failed to record the stored partner claim', error),
    )
  }
}

// Sections re-render on variant changes and on quick-add, so forms can appear after load. Catching
// submit in the capture phase covers any form this missed, including "Buy it now".
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target

    if (currentClaimId && form instanceof HTMLFormElement && form.action.includes('/cart/add')) {
      addFieldToForm(form, currentClaimId)
    }
  },
  true,
)
